import React from 'react';
import { Box, Typography, FormControl, Select, MenuItem, TextField } from '@mui/material';

const formatToday = () => {
  const d = new Date();
  // YYYY-MM-DD
  return d.toISOString().slice(0, 10);
};

const INVOICE_NUMBER_FORMAT = 'Vendor - YYYY-MM-DD - PTI Number';

const buildInvoiceNumber = ({ vendor, date, ptiNumber }) => {
  const v = String(vendor || '').trim();
  const d = String(date || '').trim();
  const p = String(ptiNumber || '').trim();
  if (!v || !d || !p) {
    return '';
  }
  return `${v}-${d}-${p}`;
};

export default function SystemInvoiceNumberSection({ vendor, ptiNumber, invoiceNumber, onChange }) {
  const vendorOptions = ['Huawei'];
  const today = formatToday();
  const computed = buildInvoiceNumber({ vendor, date: today, ptiNumber });
  const saved = (invoiceNumber ?? computed) || '';
  const showError = !String(vendor || '').trim() || !String(ptiNumber || '').trim();

  const handleVendorChange = (nextVendor) => {
    onChange({ vendor: nextVendor, ptiNumber });
  };

  const handleTextChange = (nextText) => {
    // keep it simple: allow letters/numbers/hyphen/underscore (so 001, 002 etc work)
    const cleaned = String(nextText || '').replace(/[^a-zA-Z0-9_-]/g, '');
    onChange({ vendor, ptiNumber: cleaned });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          fontSize: '18px',
          fontFamily: 'Inter',
          fontWeight: 600,
          mb: 0.5,
          display: 'flex',
          mt: 2,
        }}
      >
        Invoice Number{' '}
        <Box component="span" sx={{ color: '#d32f2f' }}>
          *
        </Box>
      </Typography>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 1.5,
          fontFamily: 'monospace',
          fontSize: '0.7rem',
          lineHeight: 1.4,
        }}
      >
        {INVOICE_NUMBER_FORMAT}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 0,
          border: '1px solid',
          borderColor: showError ? '#d32f2f' : 'divider',
          borderRadius: 1,
          backgroundColor: showError ? 'rgba(211, 47, 47, 0.04)' : 'grey.50',
          p: 1.5,
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        <FormControl
          size="small"
          required
          error={showError && !String(vendor || '').trim()}
          sx={{
            minWidth: { xs: '100%', sm: 160 },
            flex: { xs: '1 1 100%', sm: '1 1 180px' },
            maxWidth: { sm: 220 },
          }}
        >
          <Select
            displayEmpty
            value={vendor || ''}
            onChange={(e) => handleVendorChange(e.target.value)}
            sx={{ backgroundColor: '#fff', fontSize: '0.8rem' }}
          >
            <MenuItem value="">
              <em>Vendor Name</em>
            </MenuItem>
            {vendorOptions.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            alignSelf: 'center',
            px: 0.75,
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          -
        </Box>

        <TextField
          size="small"
          value={today}
          disabled
          placeholder="YYYY-MM-DD"
          sx={{
            minWidth: { xs: '100%', sm: 160 },
            flex: { xs: '1 1 100%', sm: '1 1 180px' },
            maxWidth: { sm: 220 },
            '& .MuiOutlinedInput-root': { backgroundColor: '#F9FAFB', fontSize: '0.8rem' },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
              opacity: 1,
            },
          }}
        />

        <Box
          sx={{
            alignSelf: 'center',
            px: 0.75,
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          -
        </Box>

        <TextField
          size="small"
          placeholder="PTI number (e.g. 001)"
          value={ptiNumber || ''}
          onChange={(e) => handleTextChange(e.target.value)}
          error={showError && !String(ptiNumber || '').trim()}
          sx={{
            minWidth: { xs: '100%', sm: 160 },
            flex: { xs: '1 1 100%', sm: '1 1 180px' },
            maxWidth: { sm: 220 },
            '& .MuiOutlinedInput-root': { backgroundColor: '#fff', fontSize: '0.8rem' },
          }}
        />
      </Box>

      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
        Saved Invoice Number:{' '}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {saved.trim() || '—'}
        </Box>
      </Typography>

      {showError && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#d32f2f' }}>
          Please fill all parts. Format: {INVOICE_NUMBER_FORMAT}
        </Typography>
      )}
    </Box>
  );
}
